/**
 * Novel Analysis API Client
 *
 * 小說分析系統 API 呼叫函式
 */

import { request } from './client';
import { API_BASE } from './client';

// ============================================================
// Types
// ============================================================

export interface AnalysisToolConfig {
  tool_name: string;
  display_name: string;
  description: string | null;
  provider_id: string | null;
  jailbreak_preset_id: string | null;
  temperature: number;
  max_tokens: number;
  is_enabled: boolean;
  updated_at: string;
}

export interface NovelAnalysisStatus {
  novel_id: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'failed' | 'paused';
  total_chapters: number;
  processed_chapters: number;
  current_chapter_index: number | null;
  current_step: string | null;
  error_log: Array<{ chapter_index: number; step: string; error: string; timestamp: string }>;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface ChapterAnalysisResult {
  id: string;
  novel_id: string;
  chapter_id: string;
  chapter_index: number;
  summary: string | null;
  key_events: string | null;
  timeline_markers: string | null;
  locations: string | null;
  themes: string | null;
  unresolved_threads: string | null;
  overview: string | null;
  plot_turning_points: string | null;
  sexual_records: string | null;
  character_development: string | null;
  plot_sequence: string | null;
}

export interface NovelCharacter {
  id: string;
  novel_id: string;
  name: string;
  aliases: string;
  gender: string | null;
  age: string | null;
  occupation: string | null;
  appearance: string | null;
  personality: string | null;
  psychology: string | null;
  current_state: string | null;
  charm_tendencies: string | null;
  dialogue_features: string | null;
  writing_notes: string | null;
  first_appearance_chapter: number | null;
  last_seen_chapter: number | null;
  chapter_count: number;
  is_major: boolean;
}

export interface CharacterRelationship {
  id: string;
  novel_id: string;
  character_a_id: string;
  character_b_id: string;
  character_a_name: string;
  character_b_name: string;
  relationship_type: string | null;
  address_modes: string | null;
  power_dynamic: string | null;
  description: string | null;
  perception_timeline: string;
}

export interface SexSceneResult {
  id: string;
  novel_id: string;
  chapter_id: string;
  chapter_index: number;
  scene_sequence: number;
  scene_name: string | null;
  location: string | null;
  participants: string;
  positions: string | null;
  duration: string | null;
  intensity_level: number | null;
  sensory_focus: string | null;
  character_states: string | null;
  description: string | null;
  techniques_used: string | null;
  stages: string | null;
  orgasm_records: string | null;
  cumshot_records: string | null;
  climax_records?: Array<{
    id: string;
    character_name: string;
    record_type: string;
    sequence: number;
    trigger_description: string | null;
    location_on_body: string | null;
    intensity: number | null;
    description: string | null;
  }>;
}

export interface ChapterPerspectiveResult {
  id: string;
  novel_id: string;
  chapter_id: string;
  chapter_index: number;
  observer_name: string;
  subject_name: string;
  physical_attraction: string | null;
  emotional_state: string | null;
  power_dynamic: string | null;
  charm_displayed: string | null;
  relationship_patterns: string;
  emotional_tensions: string;
  key_quotes: string;
  charm_type: string | null;
  evidence: string | null;
  plot_context: string | null;
  insights: string | null;
}

export interface WritingStyleResult {
  id: string;
  novel_id: string;
  narrative_voice: string | null;
  prose_characteristics: string | null;
  vocabulary: string | null;
  pacing: string | null;
  nsfw_style: string | null;
  sample_passages: string | null;
  style_classification: string | null;
  vocabulary_system: string | null;
  nsfw_techniques: string | null;
  quantitative: string | null;
}

export interface AnalysisResults {
  status: NovelAnalysisStatus;
  chapters: ChapterAnalysisResult[];
  characters: NovelCharacter[];
  relationships: CharacterRelationship[];
  sex_scenes: SexSceneResult[];
  perspectives: ChapterPerspectiveResult[];
  writing_style: WritingStyleResult | null;
}

// ============================================================
// Analysis API
// ============================================================

export const novelAnalysisApi = {
  // Analysis management
  startAnalysis: (novelId: string) =>
    request<{ status: string }>(`/novels/${novelId}/analysis/start`, { method: 'POST' }),

  getStatus: (novelId: string) =>
    request<NovelAnalysisStatus>(`/novels/${novelId}/analysis/status`),

  getResults: (novelId: string) =>
    request<AnalysisResults>(`/novels/${novelId}/analysis/results`),

  clearAnalysis: (novelId: string) =>
    request<{ message: string }>(`/novels/${novelId}/analysis`, { method: 'DELETE' }),

  retryAnalysis: (novelId: string) =>
    request<{ status: string }>(`/novels/${novelId}/analysis/retry`, { method: 'POST' }),

  // Individual results
  getChapterAnalysis: (novelId: string, chapterIndex: number) =>
    request<ChapterAnalysisResult>(`/novels/${novelId}/analysis/chapters/${chapterIndex}`),

  getCharacters: (novelId: string) =>
    request<NovelCharacter[]>(`/novels/${novelId}/analysis/characters`),

  getCharacterDetail: (novelId: string, charId: string) =>
    request<NovelCharacter>(`/novels/${novelId}/analysis/characters/${charId}`),

  getRelationships: (novelId: string) =>
    request<CharacterRelationship[]>(`/novels/${novelId}/analysis/relationships`),

  getWritingStyle: (novelId: string) =>
    request<WritingStyleResult>(`/novels/${novelId}/analysis/writing-style`),
};

// ============================================================
// Admin Analysis Config API
// ============================================================

export const analysisConfigApi = {
  getAll: () =>
    request<AnalysisToolConfig[]>('/admin/analysis-tools'),

  update: (toolName: string, updates: Partial<Pick<AnalysisToolConfig, 'provider_id' | 'jailbreak_preset_id' | 'temperature' | 'max_tokens' | 'is_enabled'>>) =>
    request<AnalysisToolConfig>(`/admin/analysis-tools/${toolName}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  test: async (
    toolName: string,
    params: { novel_id: string; chapter_id: string },
    handlers: {
      onEvent?: (data: unknown) => void;
      onError?: (error: Error) => void;
      onComplete?: () => void;
    },
    signal?: AbortSignal
  ): Promise<void> => {
    const response = await fetch(`${API_BASE}/admin/analysis-tools/${toolName}/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error || `HTTP ${response.status}`
      );
    }

    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          handlers.onComplete?.();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              handlers.onComplete?.();
              return;
            }
            try {
              handlers.onEvent?.(JSON.parse(data));
            } catch {
              // ignore parse errors
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      handlers.onError?.(err as Error);
    }
  },
};
